// src/api/FFmpegService.js

let ffmpegModulePromise = null;
let FFmpegClass = null;
let fetchFileFn = null;
let toBlobURLFn = null;

const resolveModule = (mod) => (mod && mod.default ? mod.default : mod);

const loadFFmpegDeps = async () => {
  if (!ffmpegModulePromise) {
    ffmpegModulePromise = Promise.all([
      import('@ffmpeg/ffmpeg'),
      import('@ffmpeg/util')
    ]).then(([ffmpegMod, utilMod]) => {
      const ffmpegResolved = ffmpegMod?.FFmpeg || ffmpegMod?.default?.FFmpeg || resolveModule(ffmpegMod);
      const utilResolved = resolveModule(utilMod);

      FFmpegClass = ffmpegResolved;
      fetchFileFn = utilResolved?.fetchFile || utilMod?.fetchFile;
      toBlobURLFn = utilResolved?.toBlobURL || utilMod?.toBlobURL;

      if (!FFmpegClass || !fetchFileFn || !toBlobURLFn) {
        throw new Error('FFmpeg 依赖加载失败');
      }
    });
  }

  await ffmpegModulePromise;
  return { FFmpeg: FFmpegClass, fetchFile: fetchFileFn, toBlobURL: toBlobURLFn };
};

class FFmpegService {
  constructor() {
    this.ffmpeg = null;
    this.loaded = false;
    this.loading = false;
    this.loadProgress = 0;
    this.loadCallbacks = [];
  }

  /**
   * 加载 FFmpeg.wasm
   * @param {Function} onProgress - 进度回调函数
   */
  async load(onProgress = null) {
    if (this.loaded) {
      console.log('[FFmpeg] 已加载，无需重复加载');
      return;
    }

    if (this.loading) {
      console.log('[FFmpeg] 正在加载中，等待完成...');
      return new Promise((resolve, reject) => {
        this.loadCallbacks.push({ resolve, reject });
      });
    }

    try {
      this.loading = true;
      console.log('[FFmpeg] 创建 FFmpeg 实例...');
      const { FFmpeg, toBlobURL } = await loadFFmpegDeps();
      this.ffmpeg = new FFmpeg();

      // 设置日志回调
      this.ffmpeg.on('log', ({ message }) => {
        console.log('[FFmpeg Log]', message);
      });

      // 设置进度回调
      this.ffmpeg.on('progress', ({ progress, time }) => {
        const progressPercent = Math.round(progress * 100);
        console.log(`[FFmpeg Progress] ${progressPercent}%, time: ${time}`);
        if (onProgress) {
          onProgress(progressPercent, time);
        }
      });

      // 加载 FFmpeg 核心文件
      // 注意：@ffmpeg/core@0.12.6 使用单线程版本，不需要 worker
      const baseURL = 'https://unpkg.com/@ffmpeg/core@0.12.6/dist/umd';
      
      console.log('[FFmpeg] CDN 基础URL:', baseURL);
      console.log('[FFmpeg] 开始下载核心文件（单线程版本）...');
      
      // 分步骤加载，便于调试
      console.log('[FFmpeg] 1/2 - 下载 ffmpeg-core.js...');
      const coreURL = await toBlobURL(`${baseURL}/ffmpeg-core.js`, 'text/javascript');
      console.log('[FFmpeg] ✓ ffmpeg-core.js 下载完成, Blob URL:', coreURL.substring(0, 60) + '...');
      
      console.log('[FFmpeg] 2/2 - 下载 ffmpeg-core.wasm...');
      const wasmURL = await toBlobURL(`${baseURL}/ffmpeg-core.wasm`, 'application/wasm');
      console.log('[FFmpeg] ✓ ffmpeg-core.wasm 下载完成, Blob URL:', wasmURL.substring(0, 60) + '...');

      console.log('[FFmpeg] 加载配置:', {
        coreURL: coreURL.substring(0, 60) + '...',
        wasmURL: wasmURL.substring(0, 60) + '...',
      });
      
      console.log('[FFmpeg] 调用 ffmpeg.load() (单线程模式)...');
      await this.ffmpeg.load({
        coreURL: coreURL,
        wasmURL: wasmURL,
      });

      this.loaded = true;
      this.loading = false;
      this.loadProgress = 100;
      
      console.log('[FFmpeg] ========== 加载完成 ✓ ==========');

      // 执行所有等待的回调
      this.loadCallbacks.forEach(({ resolve }) => resolve());
      this.loadCallbacks = [];

    } catch (error) {
      this.loading = false;
      console.error('========================================');
      console.error('[FFmpeg] ✗✗✗ 加载失败 ✗✗✗');
      console.error('========================================');
      console.error('[FFmpeg] 错误对象:', error);
      console.error('[FFmpeg] 错误消息:', error.message);
      console.error('[FFmpeg] 错误堆栈:', error.stack);
      console.error('[FFmpeg] 错误名称:', error.name);
      
      if (error.cause) {
        console.error('[FFmpeg] 错误原因:', error.cause);
      }
      
      console.error('========================================');
      
      this.loadCallbacks.forEach(({ reject }) => reject(error));
      this.loadCallbacks = [];
      
      throw new Error(`FFmpeg 加载失败: ${error.message || error}`);
    }
  }

  async downloadFile(url) {
    if (!url) {
      throw new Error('下载失败: URL 为空');
    }

    const { fetchFile } = await loadFFmpegDeps();

    // Blob URL / data URL 直接使用 fetchFile
    if (url.startsWith('blob:') || url.startsWith('data:')) {
      return await fetchFile(url);
    }

    // 始终添加时间戳绕过缓存
    const separator = url.includes('?') ? '&' : '?';
    const bustUrl = `${url}${separator}_t=${Date.now()}`;
    console.log('[FFmpegService] 下载文件 (无缓存):', bustUrl.substring(0, 100));

    try {
      const data = await fetchFile(bustUrl);
      if (data && data.byteLength > 0) {
        console.log('[FFmpegService] 下载成功，大小:', (data.byteLength / 1024 / 1024).toFixed(2), 'MB');
        return data;
      }
      throw new Error('返回空数据');
    } catch (error) {
      console.error('[FFmpegService] 下载失败:', error.message);
      throw new Error(`下载失败: ${error.message}`);
    }
  }

  /**
   * 合并视频和音频
   * @param {string} videoUrl - 视频URL或Blob URL
   * @param {string} audioUrl - 音频URL或Blob URL
   * @param {Object} options - 合并选项
   */
  async mergeVideoAudio(videoUrl, audioUrl, options = {}) {
    console.log('========================================');
    console.log('[FFmpegService] 开始合并视频和音频');
    console.log('========================================');
    
    if (!this.loaded) {
      console.error('[FFmpegService] 错误: FFmpeg 尚未加载');
      throw new Error('FFmpeg 尚未加载');
    }

    const {
      mode = 'replace',
      durationMode = 'shortest', // 新增：长度模式（shortest/video/audio）
      audioVolume = 1.0,
      originalVolume = 0.3,
      audioOffset = 0,
      fadeIn = 0,
      fadeOut = 0,
      outputFormat = 'mp4',
      onProgress = null,
    } = options;

    console.log('[FFmpegService] 输入参数:', {
      videoUrl: videoUrl.substring(0, 100) + (videoUrl.length > 100 ? '...' : ''),
      audioUrl: audioUrl.substring(0, 100) + (audioUrl.length > 100 ? '...' : ''),
      mode,
      durationMode,
      audioVolume,
      originalVolume,
      audioOffset,
      fadeIn,
      fadeOut,
      outputFormat,
    });

    try {
      // 1. 下载并写入视频文件
      console.log('[FFmpegService] 步骤1: 下载视频文件');
      console.log('[FFmpegService] 视频URL类型:', videoUrl.startsWith('blob:') ? 'Blob URL' : 'HTTP URL');
      
      try {
        const videoData = await this.downloadFile(videoUrl);
        console.log('[FFmpegService] 视频数据大小:', (videoData.byteLength / 1024 / 1024).toFixed(2), 'MB');
        
        await this.ffmpeg.writeFile('input_video.mp4', videoData);
        console.log('[FFmpegService] ✓ 视频文件写入成功: input_video.mp4');
      } catch (videoError) {
        console.error('[FFmpegService] ✗ 下载/写入视频文件失败');
        console.error('[FFmpegService] 视频错误详情:', videoError);
        throw new Error(`下载视频文件失败: ${videoError.message}`);
      }

      // 2. 下载并写入音频文件
      console.log('[FFmpegService] 步骤2: 下载音频文件');
      console.log('[FFmpegService] 音频URL类型:', audioUrl.startsWith('blob:') ? 'Blob URL' : 'HTTP URL');
      
      const audioExt = this.getFileExtension(audioUrl);
      console.log('[FFmpegService] 音频文件扩展名:', audioExt);
      
      try {
        const audioData = await this.downloadFile(audioUrl);
        console.log('[FFmpegService] 音频数据大小:', (audioData.byteLength / 1024 / 1024).toFixed(2), 'MB');
        
        await this.ffmpeg.writeFile(`input_audio.${audioExt}`, audioData);
        console.log(`[FFmpegService] ✓ 音频文件写入成功: input_audio.${audioExt}`);
      } catch (audioError) {
        console.error('[FFmpegService] ✗ 下载/写入音频文件失败');
        console.error('[FFmpegService] 音频错误详情:', audioError);
        throw new Error(`下载音频文件失败: ${audioError.message}`);
      }

      // 3. 构建 FFmpeg 命令
      console.log('[FFmpegService] 步骤3: 构建 FFmpeg 命令');
      const ffmpegArgs = this.buildFFmpegCommand(
        'input_video.mp4',
        `input_audio.${audioExt}`,
        `output.${outputFormat}`,
        {
          mode,
          durationMode,
          audioVolume,
          originalVolume,
          audioOffset,
          fadeIn,
          fadeOut,
        }
      );

      console.log('[FFmpegService] FFmpeg 命令:', 'ffmpeg ' + ffmpegArgs.join(' '));

      // 4. 执行 FFmpeg 命令
      console.log('[FFmpegService] 步骤4: 执行 FFmpeg 命令');
      try {
        await this.ffmpeg.exec(ffmpegArgs);
        console.log('[FFmpegService] ✓ FFmpeg 命令执行成功');
      } catch (execError) {
        console.error('[FFmpegService] ✗ FFmpeg 命令执行失败');
        console.error('[FFmpegService] 执行错误详情:', execError);
        throw new Error(`FFmpeg 执行失败: ${execError.message}`);
      }

      // 5. 读取输出文件
      console.log('[FFmpegService] 步骤5: 读取输出文件');
      let data;
      try {
        data = await this.ffmpeg.readFile(`output.${outputFormat}`);
        console.log('[FFmpegService] 输出文件大小:', (data.byteLength / 1024 / 1024).toFixed(2), 'MB');
        console.log('[FFmpegService] ✓ 输出文件读取成功');
      } catch (readError) {
        console.error('[FFmpegService] ✗ 读取输出文件失败');
        console.error('[FFmpegService] 读取错误详情:', readError);
        throw new Error(`读取输出文件失败: ${readError.message}`);
      }

      // 6. 创建 Blob URL
      console.log('[FFmpegService] 步骤6: 创建 Blob URL');
      const blob = new Blob([data.buffer], { type: `video/${outputFormat}` });
      const url = URL.createObjectURL(blob);
      console.log('[FFmpegService] Blob 创建成功:', {
        size: `${(blob.size / 1024 / 1024).toFixed(2)} MB`,
        type: blob.type,
        urlLength: url.length,
      });

      // 7. 清理临时文件
      console.log('[FFmpegService] 步骤7: 清理临时文件');
      await this.cleanup(['input_video.mp4', `input_audio.${audioExt}`, `output.${outputFormat}`]);
      console.log('[FFmpegService] ✓ 临时文件清理完成');

      console.log('[FFmpegService] ========== 合并完成 ==========');
      return {
        blob,
        url,
        size: blob.size,
        type: blob.type,
      };
    } catch (error) {
      console.error('========================================');
      console.error('[FFmpegService] ✗✗✗ 合并失败 ✗✗✗');
      console.error('========================================');
      console.error('[FFmpegService] 错误对象:', error);
      console.error('[FFmpegService] 错误消息:', error.message);
      console.error('[FFmpegService] 错误堆栈:', error.stack);
      console.error('========================================');
      throw error;
    }
  }

  /**
   * 构建 FFmpeg 命令参数
   */
  buildFFmpegCommand(videoFile, audioFile, outputFile, options) {
    const {
      mode,
      durationMode = 'shortest',
      audioVolume,
      originalVolume,
      audioOffset,
      fadeIn,
      fadeOut,
    } = options;

    const args = ['-i', videoFile, '-i', audioFile];

    if (mode === 'replace') {
      // 替换模式：直接用新音频替换原音频
      let audioFilter = `volume=${audioVolume}`;
      
      if (audioOffset > 0) {
        audioFilter = `adelay=${audioOffset * 1000}|${audioOffset * 1000},${audioFilter}`;
      }
      
      if (fadeIn > 0) {
        audioFilter += `,afade=t=in:st=0:d=${fadeIn}`;
      }
      
      if (fadeOut > 0) {
        audioFilter += `,afade=t=out:st=END-${fadeOut}:d=${fadeOut}`;
      }

      // 根据长度模式添加循环/填充
      if (durationMode === 'video') {
        // 保持视频长度：循环音频以匹配视频长度
        audioFilter += `,aloop=loop=-1:size=2e+09`;
      } else if (durationMode === 'audio') {
        // 保持音频长度：循环视频以匹配音频长度
        // 通过不使用 -shortest 让音频控制长度
      }

      args.push(
        '-filter_complex', `[1:a]${audioFilter}[aout]`,
        '-map', '0:v:0',
        '-map', '[aout]',
        '-c:v', 'copy',
        '-c:a', 'aac'
      );

      // 根据长度模式设置截断策略
      if (durationMode === 'shortest') {
        args.push('-shortest');
      } else if (durationMode === 'video') {
        args.push('-shortest'); // 以视频为准截断循环的音频
      }
      // durationMode === 'audio' 时不添加 -shortest，让音频决定长度
      
    } else if (mode === 'mix') {
      // 混合模式：将新音频与原音频混合
      let filter = `[0:a]volume=${originalVolume}`;
      
      // 根据长度模式为原音频添加循环
      if (durationMode === 'audio') {
        filter += `,aloop=loop=-1:size=2e+09`;
      }
      
      filter += `[a0];`;
      filter += `[1:a]volume=${audioVolume}`;
      
      if (audioOffset > 0) {
        filter += `,adelay=${audioOffset * 1000}|${audioOffset * 1000}`;
      }
      
      if (fadeIn > 0) {
        filter += `,afade=t=in:st=0:d=${fadeIn}`;
      }
      
      if (fadeOut > 0) {
        filter += `,afade=t=out:st=END-${fadeOut}:d=${fadeOut}`;
      }
      
      // 根据长度模式为新音频添加循环
      if (durationMode === 'video') {
        filter += `,aloop=loop=-1:size=2e+09`;
      }
      
      filter += `[a1];`;
      
      // 设置 amix 的 duration 策略
      if (durationMode === 'shortest') {
        filter += `[a0][a1]amix=inputs=2:duration=shortest[aout]`;
      } else if (durationMode === 'video') {
        filter += `[a0][a1]amix=inputs=2:duration=first[aout]`; // 以第一个输入（原视频音频）为准
      } else if (durationMode === 'audio') {
        filter += `[a0][a1]amix=inputs=2:duration=longest[aout]`; // 以最长的为准
      }

      args.push(
        '-filter_complex', filter,
        '-map', '0:v',
        '-map', '[aout]',
        '-c:v', 'copy',
        '-c:a', 'aac'
      );

      // 根据长度模式设置截断策略
      if (durationMode === 'video') {
        args.push('-shortest'); // 以视频为准
      }
      // shortest 模式由 amix 的 duration=shortest 处理
      // audio 模式不加 -shortest，让音频决定长度
    }

    args.push('-y', outputFile);
    return args;
  }

  /**
   * 清理临时文件
   */
  async cleanup(files) {
    for (const file of files) {
      try {
        await this.ffmpeg.deleteFile(file);
      } catch (e) {
        console.warn(`[FFmpeg] 删除文件失败: ${file}`, e);
      }
    }
  }

  /**
   * 获取文件扩展名
   */
  getFileExtension(url) {
    if (url.includes('.mp3')) return 'mp3';
    if (url.includes('.wav')) return 'wav';
    if (url.includes('.ogg')) return 'ogg';
    if (url.includes('.m4a')) return 'm4a';
    if (url.includes('.aac')) return 'aac';
    return 'mp3';
  }

  /**
   * 剪辑音频
   * @param {string} audioUrl - 音频URL或Blob URL
   * @param {Object} options - 剪辑选项
   */
  async trimAudio(audioUrl, options = {}) {
    console.log('========================================');
    console.log('[FFmpegService] 开始剪辑音频');
    console.log('========================================');

    if (!this.loaded) {
      console.error('[FFmpegService] 错误: FFmpeg 尚未加载');
      throw new Error('FFmpeg 尚未加载');
    }

    const {
      start = 0,
      end = null,
      outputFormat = 'mp3',
    } = options;

    if (start < 0) {
      throw new Error('开始时间不能小于0');
    }
    if (end !== null && end <= start) {
      throw new Error('结束时间必须大于开始时间');
    }

    const filesToCleanup = [];

    try {
      // 1. 下载并写入音频文件
      console.log('[FFmpegService] 步骤1: 下载音频文件');
      const inputExt = this.getFileExtension(audioUrl);
      const inputName = `input_audio.${inputExt}`;

      const audioData = await this.downloadFile(audioUrl);
      console.log('[FFmpegService] 音频数据大小:', (audioData.byteLength / 1024 / 1024).toFixed(2), 'MB');
      await this.ffmpeg.writeFile(inputName, audioData);
      filesToCleanup.push(inputName);
      console.log(`[FFmpegService] ✓ 音频文件写入成功: ${inputName}`);

      // 2. 构建并执行剪辑命令
      console.log('[FFmpegService] 步骤2: 执行剪辑');

      const runTrim = async (fmt) => {
        const outputName = `output.${fmt}`;
        const args = [];

        // -ss 放在输入前/后对精确度和速度影响不同。
        // 这里放在输入后，优先保证准确性。
        args.push('-i', inputName);
        args.push('-ss', String(start));
        if (end !== null) {
          args.push('-to', String(end));
        }

        // 输出编码策略：优先 mp3（可能在 wasm 中不可用），失败则回退 m4a(aac)
        if (fmt === 'mp3') {
          args.push('-c:a', 'libmp3lame');
        } else if (fmt === 'm4a' || fmt === 'aac') {
          args.push('-c:a', 'aac');
        } else {
          // 其他格式尽量保持 aac 兼容
          args.push('-c:a', 'aac');
        }

        args.push('-vn');
        args.push('-y', outputName);

        console.log('[FFmpegService] FFmpeg 命令:', 'ffmpeg ' + args.join(' '));
        await this.ffmpeg.exec(args);
        return outputName;
      };

      let finalOutputFormat = outputFormat;
      let outputName;
      try {
        outputName = await runTrim(outputFormat);
      } catch (e) {
        // mp3 编码器在部分 wasm build 中可能不可用，这里做一次兜底
        if (outputFormat === 'mp3') {
          console.warn('[FFmpegService] mp3 剪辑失败，尝试 m4a(aac) 兜底:', e);
          finalOutputFormat = 'm4a';
          outputName = await runTrim('m4a');
        } else {
          throw e;
        }
      }

      filesToCleanup.push(outputName);

      // 3. 读取输出文件
      console.log('[FFmpegService] 步骤3: 读取输出文件');
      const data = await this.ffmpeg.readFile(outputName);
      console.log('[FFmpegService] 输出文件大小:', (data.byteLength / 1024 / 1024).toFixed(2), 'MB');

      // 4. 创建 Blob URL
      console.log('[FFmpegService] 步骤4: 创建 Blob URL');
      const mimeMap = {
        mp3: 'audio/mpeg',
        m4a: 'audio/mp4',
        aac: 'audio/aac',
        wav: 'audio/wav',
        ogg: 'audio/ogg',
      };
      const blob = new Blob([data.buffer], { type: mimeMap[finalOutputFormat] || 'audio/mpeg' });
      const url = URL.createObjectURL(blob);

      // 5. 清理临时文件
      console.log('[FFmpegService] 步骤5: 清理临时文件');
      await this.cleanup(filesToCleanup);
      console.log('[FFmpegService] ✓ 临时文件清理完成');

      console.log('[FFmpegService] ========== 剪辑完成 ==========');
      return {
        blob,
        url,
        size: blob.size,
        type: blob.type,
        outputFormat: finalOutputFormat,
      };
    } catch (error) {
      console.error('========================================');
      console.error('[FFmpegService] ✗✗✗ 剪辑失败 ✗✗✗');
      console.error('========================================');
      console.error('[FFmpegService] 错误对象:', error);
      console.error('[FFmpegService] 错误消息:', error.message);
      console.error('[FFmpegService] 错误堆栈:', error.stack);
      console.error('========================================');

      await this.cleanup(filesToCleanup);
      throw error;
    }
  }

  /**
   * 拼接多个视频（保留各自原音频）
   * @param {string[]} videoUrls - 视频URL数组
   * @param {Object} options - 拼接选项
   */
  async concatVideos(videoUrls, options = {}) {
    console.log('========================================');
    console.log('[FFmpegService] 开始拼接视频');
    console.log('========================================');
    
    if (!this.loaded) {
      console.error('[FFmpegService] 错误: FFmpeg 尚未加载');
      throw new Error('FFmpeg 尚未加载');
    }

    if (!videoUrls || videoUrls.length < 2) {
      throw new Error('至少需要2个视频进行拼接');
    }

    const {
      outputFormat = 'mp4',
      onProgress = null,
    } = options;

    console.log('[FFmpegService] 输入参数:', {
      videoCount: videoUrls.length,
      outputFormat,
    });

    const inputFiles = [];
    const filesToCleanup = [];

    try {
      // 1. 下载并写入所有视频文件
      console.log('[FFmpegService] 步骤1: 下载所有视频文件');
      
      for (let i = 0; i < videoUrls.length; i++) {
        const videoUrl = videoUrls[i];
        const inputFileName = `input_video_${i}.mp4`;
        
        console.log(`[FFmpegService] 下载视频 ${i + 1}/${videoUrls.length}:`, 
          videoUrl.substring(0, 80) + (videoUrl.length > 80 ? '...' : ''));
        
        try {
          const videoData = await this.downloadFile(videoUrl);
          console.log(`[FFmpegService] 视频 ${i + 1} 大小:`, (videoData.byteLength / 1024 / 1024).toFixed(2), 'MB');
          
          await this.ffmpeg.writeFile(inputFileName, videoData);
          inputFiles.push(inputFileName);
          filesToCleanup.push(inputFileName);
          console.log(`[FFmpegService] ✓ 视频文件写入成功: ${inputFileName}`);
        } catch (videoError) {
          console.error(`[FFmpegService] ✗ 下载/写入视频 ${i + 1} 失败`);
          throw new Error(`下载视频 ${i + 1} 失败: ${videoError.message}`);
        }
        
        if (onProgress) {
          onProgress(Math.round(((i + 1) / videoUrls.length) * 30)); // 0-30%
        }
      }

      // 2. 创建 concat 文件列表
      console.log('[FFmpegService] 步骤2: 创建拼接文件列表');
      const concatListContent = inputFiles.map(f => `file '${f}'`).join('\n');
      console.log('[FFmpegService] 拼接列表内容:\n', concatListContent);
      
      const encoder = new TextEncoder();
      await this.ffmpeg.writeFile('concat_list.txt', encoder.encode(concatListContent));
      filesToCleanup.push('concat_list.txt');
      console.log('[FFmpegService] ✓ 拼接列表文件创建成功');

      // 3. 构建并执行 FFmpeg 命令
      console.log('[FFmpegService] 步骤3: 执行视频拼接');
      
      // 使用 concat demuxer（适用于相同编码格式的视频，速度快）
      const ffmpegArgs = [
        '-f', 'concat',
        '-safe', '0',
        '-i', 'concat_list.txt',
        '-c', 'copy',  // 直接复制，不重新编码
        '-y',
        `output.${outputFormat}`
      ];
      
      console.log('[FFmpegService] FFmpeg 命令:', 'ffmpeg ' + ffmpegArgs.join(' '));

      try {
        await this.ffmpeg.exec(ffmpegArgs);
        console.log('[FFmpegService] ✓ 视频拼接成功');
      } catch (execError) {
        console.warn('[FFmpegService] 快速拼接失败，尝试重新编码模式...');
        
        // 如果直接复制失败，使用重新编码模式
        const reencodeArgs = [
          '-f', 'concat',
          '-safe', '0',
          '-i', 'concat_list.txt',
          '-c:v', 'libx264',
          '-c:a', 'aac',
          '-preset', 'fast',
          '-y',
          `output.${outputFormat}`
        ];
        
        console.log('[FFmpegService] 重新编码命令:', 'ffmpeg ' + reencodeArgs.join(' '));
        await this.ffmpeg.exec(reencodeArgs);
        console.log('[FFmpegService] ✓ 重新编码拼接成功');
      }
      
      filesToCleanup.push(`output.${outputFormat}`);

      if (onProgress) {
        onProgress(80);
      }

      // 4. 读取输出文件
      console.log('[FFmpegService] 步骤4: 读取输出文件');
      let data;
      try {
        data = await this.ffmpeg.readFile(`output.${outputFormat}`);
        console.log('[FFmpegService] 输出文件大小:', (data.byteLength / 1024 / 1024).toFixed(2), 'MB');
        console.log('[FFmpegService] ✓ 输出文件读取成功');
      } catch (readError) {
        console.error('[FFmpegService] ✗ 读取输出文件失败');
        throw new Error(`读取输出文件失败: ${readError.message}`);
      }

      if (onProgress) {
        onProgress(90);
      }

      // 5. 创建 Blob URL
      console.log('[FFmpegService] 步骤5: 创建 Blob URL');
      const blob = new Blob([data.buffer], { type: `video/${outputFormat}` });
      const url = URL.createObjectURL(blob);
      console.log('[FFmpegService] Blob 创建成功:', {
        size: `${(blob.size / 1024 / 1024).toFixed(2)} MB`,
        type: blob.type,
      });

      // 6. 清理临时文件
      console.log('[FFmpegService] 步骤6: 清理临时文件');
      await this.cleanup(filesToCleanup);
      console.log('[FFmpegService] ✓ 临时文件清理完成');

      if (onProgress) {
        onProgress(100);
      }

      console.log('[FFmpegService] ========== 拼接完成 ==========');
      return {
        blob,
        url,
        size: blob.size,
        type: blob.type,
      };
    } catch (error) {
      console.error('========================================');
      console.error('[FFmpegService] ✗✗✗ 拼接失败 ✗✗✗');
      console.error('========================================');
      console.error('[FFmpegService] 错误对象:', error);
      console.error('[FFmpegService] 错误消息:', error.message);
      console.error('[FFmpegService] 错误堆栈:', error.stack);
      console.error('========================================');
      
      // 清理已创建的临时文件
      await this.cleanup(filesToCleanup);
      
      throw error;
    }
  }

  /**
   * 获取加载状态
   */
  getLoadStatus() {
    return {
      loaded: this.loaded,
      loading: this.loading,
      progress: this.loadProgress,
    };
  }

  /**
   * 卸载 FFmpeg
   */
  async unload() {
    if (this.ffmpeg && this.loaded) {
      await this.ffmpeg.terminate();
      this.ffmpeg = null;
      this.loaded = false;
    }
  }
}

export default new FFmpegService();
